import {BrowserModule} from '@angular/platform-browser';
import {APP_INITIALIZER, ErrorHandler, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {SideNavComponent} from './side-nav/side-nav.component';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {LoginComponent} from './login/login.component';
import {JwtInterceptor} from './_helpers/jwt.interceptor';
import {SchedulingStoreInterceptor} from './_helpers/scheduling-store.interceptor';
import {LoginLayoutComponent} from './login-layout/login-layout.component';
import {HomeLayoutComponent} from './home-layout/home-layout.component';
import {FlexLayoutModule} from '@angular/flex-layout';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS} from '@angular/material-moment-adapter';
import {FormUtils} from './_helpers/form-utils';
import {ForgotPasswordComponent} from './login/forgot-password/forgot-password.component';
import {PasswordRestConfirmComponent} from './login/password-rest-confirm/password-rest-confirm.component';
import {LoaderInterceptor} from './_helpers/loader.interceptor';
import {LogRocketInterceptor} from './interceptors/logrocket.interceptor';
import {CustomErrorHandlerService} from './_helpers/error-handler';
import {ReportDialogComponent} from './dashboard/report/report-dialog/report-dialog.component';
import {CommonModule, HashLocationStrategy, LocationStrategy} from '@angular/common';
import {OverlayContainer} from '@angular/cdk/overlay';
import {IMqttServiceOptions, MqttModule} from 'ngx-mqtt';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatIconModule} from '@angular/material/icon';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatChipsModule} from '@angular/material/chips';
import {MatDialogModule} from '@angular/material/dialog';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatMenuModule} from '@angular/material/menu';
import {MatSelectModule} from '@angular/material/select';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {HolidaysComponent} from './holidays/holidays.component';
import {RouterModule} from '@angular/router';
import {MatSnackBarModule, MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarConfig} from '@angular/material/snack-bar';
import {MatListModule} from '@angular/material/list';
import {NgIdleKeepaliveModule} from '@ng-idle/keepalive';
import {MatNativeDateModule} from '@angular/material/core';
import {MatButtonModule} from '@angular/material/button';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {TerminalStatusComponent} from './terminal-status/terminal-status.component';
import {MatTooltipModule} from '@angular/material/tooltip';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ServiceWorkerModule} from '@angular/service-worker';
import {environment} from '../environments/environment';
import { ApiKeyToken, ScriptLoaderService } from '@angular-material-extensions/google-maps-autocomplete';
import { CallCardComponent } from './call-card/call-card.component';
import { CoreModule } from './core.module';
import { TrainingModeBannerComponent } from './training-mode-banner/training-mode-banner.component';
import {Capacitor} from '@capacitor/core';
import { SafeCurrencyPipe } from './_pipes/safe-currency.pipe';
import { SafeNumberPipe } from './_pipes/safe-number.pipe';


export const MQTT_SERVICE_OPTIONS: IMqttServiceOptions = {
  hostname: 'labrador-o58365.a01.euc1.aws.hivemq.cloud',
  port: 8884,
  protocol: 'wss',
  path: '/mqtt',
  clean: true,
  username: 'labrador',
  password: 'kUbS6EyzcXYU8R',
  connectTimeout: 4000,
  reconnectPeriod: 4000,
  clientId: 'labmq-business-' + localStorage.getItem('mqttsession')
};

const allowedHosts = ['business.text-order.com', 'business.beta.labrador.ai'];

/** See the MAT_SNACK_BAR_DEFAULT_OPTIONS provider below. `pos-mobile-orders` is the class
 *  orders.component puts on <body> while its phone shell is active. */
export function posSnackBarDefaults(): MatSnackBarConfig {
  const cfg = new MatSnackBarConfig();
  Object.defineProperty(cfg, 'verticalPosition', {
    enumerable: true,
    get: () => (typeof document !== 'undefined' && document.body.classList.contains('pos-mobile-orders')) ? 'top' : 'bottom',
  });
  return cfg;
}

@NgModule({
    declarations: [
      AppComponent,
      SideNavComponent,
      LoginComponent,
      LoginLayoutComponent,
      HomeLayoutComponent,
      ForgotPasswordComponent,
      PasswordRestConfirmComponent,
      ReportDialogComponent,
      HolidaysComponent,
      CallCardComponent,
      TrainingModeBannerComponent
    ],
    imports: [
      CoreModule,
      CommonModule,
      RouterModule,
      FlexLayoutModule,
      FormsModule,
      ReactiveFormsModule,
      BrowserModule,
      AppRoutingModule,
      MatAutocompleteModule,
      MatChipsModule,
      MatIconModule,
      MatInputModule,
      MatFormFieldModule,
      MatMenuModule,
      MatSidenavModule,
      MatToolbarModule,
      MatDialogModule,
      MatSlideToggleModule,
      MatSelectModule,
      MatDatepickerModule,
      MatNativeDateModule,
      MatListModule,
      MatSnackBarModule,
      NgIdleKeepaliveModule.forRoot(),
      MqttModule.forRoot(MQTT_SERVICE_OPTIONS),
      MatButtonModule,
      MatProgressBarModule,
      MatTooltipModule,
      BrowserAnimationsModule,
      SafeCurrencyPipe,
      SafeNumberPipe,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.swEnabled && !Capacitor.isNativePlatform(),
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000'
      }),
    ],
    providers: [
      { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: LogRocketInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: SchedulingStoreInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
      { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: false } },
      // ENG-5566: the plain container, which always lives in <body>. FullscreenOverlayContainer
      // moves it into document.fullscreenElement, and the only element this app ever makes
      // fullscreen is <html> (orders / home-layout `this.elem = document.documentElement`), so
      // in fullscreen every dialog ended up OUTSIDE <body>. All Orders dark-mode rules are
      // scoped to `body.dark-mode-active …` and its --pos-* tokens inherit from <body>, so the
      // payment-type picker and every other dialog rendered light until a reload dropped
      // fullscreen. <html> being fullscreen still shows everything in <body>, overlays included.
      //
      // Superseded (kept per convention):
      // { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
      { provide: OverlayContainer, useClass: OverlayContainer },
      // Snackbars sit at the TOP while the phone Orders shell is on screen (PAX toasts sit
      // over the content, and the phone bottom bar / keypad would hide a bottom snackbar).
      // Provided at ROOT — MatSnackBar reads this token once when it is constructed in the
      // root injector, so a module-level override would not propagate (see
      // scheduling-snack.service.ts). MatSnackBar merges it with `{...defaults, ...config}`
      // on EVERY open(), so a getter here is evaluated per call and follows the shell's
      // body class live. Desktop keeps Material's default ('bottom').
      { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useFactory: posSnackBarDefaults },
      { provide: LocationStrategy, useClass: HashLocationStrategy },
      { provide: ErrorHandler, useClass: CustomErrorHandlerService },
      // The upgraded @angular-material-extensions/google-maps-autocomplete injects
      // ApiKeyToken in its component/directive constructors, so it must be provided
      // or they throw NullInjectorError. Provided once here so every feature module
      // importing MatGoogleMapsAutocompleteModule (orders, info, influencer)
      // resolves it, rather than calling forRoot() in each one.
      { provide: ApiKeyToken, useValue: environment.placesAPIKey },
      // index.html already loads the Maps script (synchronously, so
      // <google-map> can read google.maps in ngOnInit). Mark that same URL as
      // loaded in the autocomplete library's ScriptLoaderService so it reuses
      // it instead of appending a second tag -- its cache is in-memory only and
      // does not look for existing tags in the DOM.
      {
        provide: APP_INITIALIZER,
        multi: true,
        deps: [ScriptLoaderService],
        useFactory: (loader: ScriptLoaderService) => () => {
          const src = `https://maps.googleapis.com/maps/api/js?key=${environment.placesAPIKey}&libraries=places`;
          (loader as any).loadedScripts[src] = true;
        },
      },
      FormUtils,
      provideHttpClient(withInterceptorsFromDi())
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
